// upgrade-prompt.js — handle the dashboard side of HTTP 402
// upgrade_required responses (PR D of the Free-tier gating arc).
//
// PR C added server-side gates: createBu, createRole, and the
// existing createInvitations + redeemInvite paths return a 402
// with a structured body when a Free-tier tenant tries to use a
// Team feature. PR D consumes that body and renders an upgrade
// prompt → Stripe checkout flow instead of a generic error toast.
//
// The api.js layer parses the 402 and throws an Error with
// .upgradeRequired = true plus the structured fields attached.
// Callers wrap their apiWithMessage await in try/catch and:
//
//   if (err && err.upgradeRequired) {
//     await showUpgradeRequiredFromError(err);
//     return;
//   }
//   // … existing error handling
//
// showUpgradeRequiredFromError renders a confirm dialog with
// feature-aware copy and the "Upgrade to Team" CTA. The CTA fires
// routeBillingChange(err.upgradeTo) — the same routing Account →
// Billing uses (in-place change-plan for already-paid tenants,
// Stripe Checkout otherwise).

import { showConfirm } from "./ui.js";
import { routeBillingChange } from "./billing.js";

// Friendly tier-label map — DB-side identifiers come back on
// err.upgradeTo and err.currentPlan. The pricing rename (PR #136)
// kept DB values as 'solo'/'startup'/'enterprise'.
function tierLabel(dbIdentifier) {
  if (dbIdentifier === "enterprise") return "Enterprise";
  if (dbIdentifier === "startup") return "Team";
  return "Free";
}

// showUpgradeRequiredFromError renders the upgrade dialog using
// the structured err properties. Returns a Promise that resolves
// to true if the user clicked the upgrade CTA (and startCheckout
// has been kicked off — the browser will navigate to Stripe and
// the dashboard state becomes moot), false if they cancelled.
//
// The dialog body is HTML — showConfirm renders it via innerHTML.
// All interpolated strings flow through escapeHTML-equivalent safe
// patterns (the err fields come from coordinator.ts JSON, which is
// either constants or escaped) but we treat err.feature defensively
// since it could in theory carry future server values we haven't
// audited.
export async function showUpgradeRequiredFromError(err) {
  if (!err || !err.upgradeRequired) return false;

  // Default to upgrading to Team if upgradeTo wasn't specified —
  // covers any defensive case where a future server gate emits the
  // 402 without setting upgrade_to.
  const target = err.upgradeTo || "startup";
  const targetLabel = tierLabel(target);
  const featureName = err.feature || "This feature";
  // Strip the "Custom" prefix off the feature name for the headline
  // since "Upgrade to unlock Custom workspaces" reads more naturally
  // than "Custom workspaces are a Team feature." But fall back to
  // the original phrasing for unknown features.
  const friendlyFeature = featureName.toLowerCase();

  // Trial copy only when the server says this identity is actually eligible
  // (`trial_eligible` on /v1/billing/status, cached on window._billing).
  // Unknown (no billing snapshot yet) counts as NOT eligible — promising a
  // trial the checkout won't grant is worse than omitting the line.
  const trialEligible = !!(window._billing && window._billing.trial_eligible === true);
  const trialLine = trialEligible
    ? `
      <p style="margin: var(--s3) 0 0; font-size: 12px; color: var(--text-tertiary);">
        14-day free trial. No credit card required.
      </p>`
    : "";

  const bodyHTML = `
    <div style="font-size: 13px; color: var(--text); line-height: 1.6;">
      <p style="margin: 0 0 var(--s3);"><strong>${escapeText(featureName)}</strong> are a <strong>${escapeText(targetLabel)}</strong> feature.</p>
      <p style="margin: 0; color: var(--text-secondary);">
        Upgrade your team to ${escapeText(targetLabel)} to unlock ${escapeText(friendlyFeature)},
        cross-machine sync, team invitations, and the team dashboard.
        Your existing Free-tier work is preserved.
      </p>${trialLine}
    </div>`;

  const ok = await showConfirm(
    `Upgrade to ${targetLabel}`,
    bodyHTML,
    `Upgrade to ${targetLabel}`,
  );
  if (!ok) return false;

  // Route like Account → Billing does: an already-paid tenant gets an
  // in-place change-plan, everyone else gets Checkout. Calling startCheckout
  // directly here used to send startup-tier admins hitting an Enterprise
  // feature gate into a SECOND checkout session instead of a plan change.
  try {
    await routeBillingChange(target);
  } catch (e) {
    // routeBillingChange's branches already showed a toast; just don't crash.
    console.warn("[upgrade-prompt] upgrade routing failed:", e);
  }
  return true;
}

// Minimal HTML-escape for safe interpolation into the dialog body.
// Server values shouldn't contain unsafe characters but defensive.
// Includes single-quote escape for defense-in-depth even though the
// current call sites use it in element-body context (where single
// quotes can't break the string) — keeps the function safe if it's
// ever copied to an attribute context like `onclick='...'`.
function escapeText(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
